import { QuestionMarkCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import guideUrl from '@/assets/payment-guide.jpg';
import { Button } from '@/components/ui/Button';
import { IconButton } from '@/components/ui/IconButton';

/** Intrinsic pixels of `src/assets/payment-guide.jpg`, so the box reserves space before decode. */
const GUIDE_WIDTH = 935;
const GUIDE_HEIGHT = 1024;

/**
 * Player-opened sheet for the static EasyPaisa steps. Local state only: it submits nothing and
 * never writes a payment outcome (ADR-043).
 */
export function PaymentGuide() {
  const { t } = useTranslation('cashier');
  const { t: tCommon } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const titleId = useId();
  const hintId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const trigger = triggerRef.current;
    const root = document.getElementById('root');
    if (root !== null) root.inert = true;
    const previousOverflow = document.body.style.overflow;
    document.body.style.setProperty('overflow', 'hidden');
    dialogRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.setProperty('overflow', previousOverflow);
      if (root !== null) root.inert = false;
      trigger?.focus();
    };
  }, [open]);

  const close = () => {
    setOpen(false);
  };

  return (
    <>
      <Button
        ref={triggerRef}
        type="button"
        variant="secondary"
        size="sm"
        className="ms-auto shrink-0"
        startIcon={<QuestionMarkCircleIcon className="size-5" />}
        onClick={() => {
          setOpen(true);
        }}
      >
        {t('guide.open')}
      </Button>
      {open &&
        createPortal(
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={hintId}
            tabIndex={-1}
            className="fixed inset-0 z-50 flex p-3 outline outline-transparent sm:p-6"
          >
            <div className="border-border bg-surface relative z-10 m-auto flex max-h-[calc(100dvh-1.5rem)] w-full max-w-3xl flex-col overflow-hidden rounded-lg border sm:max-h-[calc(100dvh-3rem)]">
              <div className="border-border flex items-center gap-2 border-b px-4 py-2">
                <div className="min-w-0 flex-1">
                  <h2 id={titleId} className="text-text text-base font-semibold">
                    {t('guide.title')}
                  </h2>
                  <p id={hintId} className="text-text-muted text-sm leading-relaxed">
                    {t('guide.hint')}
                  </p>
                </div>
                <IconButton
                  aria-label={tCommon('actions.close')}
                  icon={<XMarkIcon />}
                  onClick={close}
                />
              </div>
              <div className="min-h-0 flex-1 overflow-auto overscroll-contain">
                <img
                  src={guideUrl}
                  alt={t('guide.imageAlt')}
                  width={GUIDE_WIDTH}
                  height={GUIDE_HEIGHT}
                  decoding="async"
                  className="h-auto w-full"
                />
              </div>
            </div>
            {/*
             * The text token flips to near-white in dark mode, so it cannot dim the page.
             * Kept out of tab order; the header control is the keyboard close.
             */}
            <button
              type="button"
              tabIndex={-1}
              className="absolute inset-0 z-0 bg-black/70"
              aria-label={tCommon('actions.close')}
              onClick={close}
            />
          </div>,
          document.body,
        )}
    </>
  );
}
